import { Toast as Kobalte, toaster as libraryToaster, useToastContext } from "@kobalte/core/toast"
import type { ToastRootProps, ToastCloseButtonProps, ToastTitleProps, ToastDescriptionProps } from "@kobalte/core/toast"
import type { ComponentProps, JSX } from "solid-js"
import { Match, Show, Switch, createEffect, createSignal, onCleanup, onMount, splitProps } from "solid-js"
import { Portal } from "solid-js/web"
import { useI18n } from "../context/i18n"
import { Icon, type IconProps } from "./icon"
import { IconButton } from "./icon-button"
import { Spinner } from "./spinner"
import { createVisualViewportInset } from "../utils/visual-viewport-inset"
import { createToastQueue } from "./toast-queue"

type ToastPresentation = (props: { toastId: number; dismiss: () => void; onDismissReady: (close: () => void) => void }) => JSX.Element
const queue = createToastQueue<ToastPresentation>()
export const toaster = { dismiss: queue.dismiss, clear: queue.clear }

export interface ToastRegionProps extends ComponentProps<typeof Kobalte.Region> {}

function ToastRegion(props: ToastRegionProps) {
  const inset = createVisualViewportInset(() => true)
  let connection: ReturnType<typeof queue.connect> | undefined
  onMount(() => {
    connection = queue.connect((entry, removed) => {
      let close: (() => void) | undefined
      let closing = false
      libraryToaster.show(props => {
        onCleanup(removed)
        return entry.value({
          toastId: props.toastId,
          dismiss: () => queue.dismiss(entry.id),
          onDismissReady: value => { close = value; if (closing) close() },
        })
      }, { region: entry.persistent ? "attention" : undefined })
      return () => { closing = true; close?.() }
    }, props.limit ?? 3)
  })
  createEffect(() => connection?.setLimit(props.limit ?? 3))
  onCleanup(() => { connection?.dispose(); libraryToaster.clear() })
  return (
    <Portal>
      <div data-component="toast-region" style={{ "--toast-viewport-inset": `${inset()}px` }}>
        <Kobalte.Region regionId="attention" aria-label="Notifications requiring action" limit={3}>
          <Kobalte.List data-slot="toast-list" />
        </Kobalte.Region>
        <Kobalte.Region {...props} limit={Number.POSITIVE_INFINITY}>
          <Kobalte.List data-slot="toast-list" />
        </Kobalte.Region>
      </div>
    </Portal>
  )
}

export interface ToastRootComponentProps extends ToastRootProps {
  class?: string
  classList?: ComponentProps<"li">["classList"]
  children?: JSX.Element
  onDismissReady?: (close: () => void) => void
}

function ToastDismissHandle(props: { ready?: (close: () => void) => void }) {
  props.ready?.(useToastContext().close)
  return null
}

function ToastRoot(props: ToastRootComponentProps) {
  const [local, others] = splitProps(props, ["onDismissReady", "children"])
  return (
    <Kobalte
      data-component="toast"
      classList={{
        ...(props.classList ?? {}),
        [props.class ?? ""]: !!props.class,
      }}
      {...others}
    >
      <ToastDismissHandle ready={local.onDismissReady} />
      {local.children}
    </Kobalte>
  )
}

function ToastIcon(props: { name: IconProps["name"] }) {
  return (
    <div data-slot="toast-icon">
      <Icon name={props.name} />
    </div>
  )
}

function ToastContent(props: ComponentProps<"div">) {
  return <div data-slot="toast-content" {...props} />
}

function ToastTitle(props: ToastTitleProps & ComponentProps<"div">) {
  return <Kobalte.Title data-slot="toast-title" {...props} />
}

function ToastDescription(props: ToastDescriptionProps & ComponentProps<"div">) {
  return <Kobalte.Description data-slot="toast-description" {...props} />
}

function ToastActions(props: ComponentProps<"div">) {
  return <div data-slot="toast-actions" {...props} />
}

function ToastCloseButton(props: ToastCloseButtonProps & ComponentProps<"button">) {
  const i18n = useI18n()
  return (
    <Kobalte.CloseButton
      data-slot="toast-close-button"
      as={IconButton}
      icon="close"
      variant="ghost"
      aria-label={i18n.t("ui.common.dismiss")}
      {...props}
    />
  )
}

function ToastProgressTrack(props: ComponentProps<typeof Kobalte.ProgressTrack>) {
  return <Kobalte.ProgressTrack data-slot="toast-progress-track" {...props} />
}

function ToastProgressFill(props: ComponentProps<typeof Kobalte.ProgressFill>) {
  return <Kobalte.ProgressFill data-slot="toast-progress-fill" {...props} />
}

export const Toast = Object.assign(ToastRoot, {
  Region: ToastRegion,
  Icon: ToastIcon,
  Content: ToastContent,
  Title: ToastTitle,
  Description: ToastDescription,
  Actions: ToastActions,
  CloseButton: ToastCloseButton,
  ProgressTrack: ToastProgressTrack,
  ProgressFill: ToastProgressFill,
})

export type ToastVariant = "default" | "success" | "error" | "loading"

export interface ToastAction {
  label: string
  onClick: "dismiss" | (() => void)
}

export interface ToastOptions {
  title?: string
  description?: string
  icon?: IconProps["name"]
  variant?: ToastVariant
  duration?: number
  persistent?: boolean
  actions?: ToastAction[]
}

export interface AttentionToastOptions extends Omit<ToastOptions, "persistent"> {
  actions: ToastAction[]
}

function renderToast(opts: ToastOptions) {
  const icon = opts.icon ?? (opts.variant === "error" ? "circle-x" : opts.variant === "success" ? "circle-check" : undefined)
  return queue.show((props) => (
    <Toast
      toastId={props.toastId}
      onDismissReady={props.onDismissReady}
      duration={opts.duration}
      persistent={opts.persistent}
      data-variant={opts.variant ?? "default"}
    >
      <Switch>
        <Match when={icon}>
          <Toast.Icon name={icon!} />
        </Match>
        <Match when={opts.variant === "loading"}>
          <div data-slot="toast-icon">
            <Spinner />
          </div>
        </Match>
      </Switch>
      <Toast.Content>
        <Show when={opts.title}>
          <Toast.Title>{opts.title}</Toast.Title>
        </Show>
        <Show when={opts.description}>
          <Toast.Description>{opts.description}</Toast.Description>
        </Show>
        <Show when={opts.actions?.length}>
          <Toast.Actions>
            {opts.actions!.map((action) => (
              <button
                data-slot="toast-action"
                onClick={() => {
                  if (typeof action.onClick === "function") {
                    action.onClick()
                  }
                  props.dismiss()
                }}
              >
                {action.label}
              </button>
            ))}
          </Toast.Actions>
        </Show>
      </Toast.Content>
      <Toast.CloseButton />
    </Toast>
  ), opts.persistent)
}

export function showToast(options: ToastOptions | string) {
  return renderToast(typeof options === "string" ? { description: options } : options)
}

export function showAttentionToast(options: AttentionToastOptions) {
  return renderToast({ ...options, persistent: true })
}

export interface ToastPromiseOptions<T, U = unknown> {
  loading?: JSX.Element
  success?: (data: T) => JSX.Element
  error?: (error: U) => JSX.Element
}

export function showPromiseToast<T, U = unknown>(
  promise: Promise<T> | (() => Promise<T>),
  options: ToastPromiseOptions<T, U>,
) {
  const [result, setResult] = createSignal<{ state: "pending" } | { state: "fulfilled"; data: T } | { state: "rejected"; error: U }>({ state: "pending" })
  void Promise.resolve().then(() => typeof promise === "function" ? promise() : promise).then(
    data => setResult({ state: "fulfilled", data }),
    (error: U) => setResult({ state: "rejected", error }),
  )
  return queue.show((props) => (
    <Toast
      toastId={props.toastId}
      onDismissReady={props.onDismissReady}
      data-variant={result().state === "pending" ? "loading" : result().state === "fulfilled" ? "success" : "error"}
    >
      <Toast.Content>
        <Toast.Description>
          {(() => {
            const value = result()
            return value.state === "pending" ? options.loading : value.state === "fulfilled" ? options.success?.(value.data) : options.error?.(value.error)
          })()}
        </Toast.Description>
      </Toast.Content>
      <Toast.CloseButton />
    </Toast>
  ))
}
